import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Popover } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonFormGrid } from 'components/common/form/grid';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { MachineSettingsDateRangeInput } from 'components/sections/admin-portal/machines/dialogs/settings/common/machine-settings-date-range-input';
import { useMachineMissedDetectionsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineMissedDetectionsDialogState';
import { useMachineShotHistoryDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineShotHistoryDialogState';
import { HistoryTab } from 'enums/machine-settings';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { first } from 'lodash';
import { useState } from 'react';
import { AdminVisionDetectionsService } from 'services/admin/vision-detections';
import { SessionEventsService } from 'services/session-events.service';

export const MissedDetectionsHistoryToolbar = () => {
  const {
    missedDetectionFilters,
    setMissedDetectionsFilters,
    setMissedDetectionID,
  } = useMachineMissedDetectionsDialogState();

  const { setShotID, setHistoryTab } = useMachineShotHistoryDialogState();

  const [fireID, setFireID] = useState('');
  const [loadingFire, setLoadingFire] = useState(false);
  const [showFireInput, setShowFireInput] = useState(false);

  const findMissedDetectionByFireID = async () => {
    setLoadingFire(true);

    const fire = await SessionEventsService.getFireByID(fireID);

    if (!fire) {
      NotifyHelper.warning({
        message_md: `No fire found for ID ${fireID}`,
      });

      setLoadingFire(false);
      return;
    }

    const missedDetection =
      await AdminVisionDetectionsService.getInstance().getByFireID(fireID);

    if (missedDetection) {
      setLoadingFire(false);

      setMissedDetectionID(missedDetection._id);
      setShowFireInput(false);

      setFireID('');
      return;
    }

    // if we can't find a missed detection, try finding a shot / successful detection and redirecting the user
    const shot = first(fire?.shot);

    if (shot) {
      NotifyHelper.warning({
        message_md: `Found a shot for fire ID ${fireID} but no missed detection. Redirecting to shot details.`,
      });

      setShotID(shot._id);
      setHistoryTab(HistoryTab.ShotHistory);
      setShowFireInput(false);
      setFireID('');
      setLoadingFire(false);
      return;
    }

    NotifyHelper.warning({
      message_md: `No shot or missed detection found for fire ID ${fireID}`,
    });

    setLoadingFire(false);
  };

  return (
    <CommonFormGrid columns={4} gap={RADIX.FLEX.GAP.SM}>
      <MachineSettingsDateRangeInput
        dateRange={missedDetectionFilters.dateRange}
        onChange={(dateRange) => setMissedDetectionsFilters({ dateRange })}
        showTime
      />
      <CommonTextInput
        id="missed-detection-message"
        name="message"
        placeholder="Message"
        value={missedDetectionFilters.message}
        onChange={(v) => setMissedDetectionsFilters({ message: v })}
        debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
        rightSlot={
          missedDetectionFilters.message.length === 0 ? (
            <MagnifyingGlassIcon />
          ) : (
            <Cross2Icon
              onClick={() => {
                setMissedDetectionsFilters({ message: '' });
              }}
              className="cursor-pointer"
            />
          )
        }
      />
      <CommonTextInput
        id="missed-detection-pitch-name"
        name="name"
        placeholder="Pitch Name"
        value={missedDetectionFilters.pitchName}
        onChange={(v) => setMissedDetectionsFilters({ pitchName: v })}
        debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
        rightSlot={
          missedDetectionFilters.pitchName.length === 0 ? (
            <MagnifyingGlassIcon />
          ) : (
            <Cross2Icon
              onClick={() => {
                setMissedDetectionsFilters({ pitchName: '' });
              }}
              className="cursor-pointer"
            />
          )
        }
      />
      <Popover.Root
        open={showFireInput}
        onOpenChange={(open) => {
          setShowFireInput(open);

          if (!open) {
            setFireID('');
          }
        }}
      >
        <Popover.Trigger>
          <Button variant={RADIX.BUTTON.VARIANT.SECONDARY}>Fire ID</Button>
        </Popover.Trigger>
        <Popover.Content asChild align="end">
          <Card>
            {/* so the user can submit by hitting Enter */}
            <form>
              <Flex
                p="1"
                align="center"
                gap={RADIX.FLEX.GAP.SM}
                minWidth="300px"
              >
                <Box flexGrow="1">
                  <CommonTextInput
                    id="missed-detection-fire-id"
                    value={fireID}
                    onChange={(v) => setFireID(v ?? '')}
                    disabled={loadingFire}
                    placeholder="Enter your Fire ID"
                    optional
                  />
                </Box>
                <Button
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                  color={RADIX.COLOR.SUCCESS}
                  loading={loadingFire}
                  onClick={findMissedDetectionByFireID}
                >
                  Submit
                </Button>
              </Flex>
            </form>
          </Card>
        </Popover.Content>
      </Popover.Root>
    </CommonFormGrid>
  );
};
